import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { showToast } from "../../utils/toast";
import { useAuth } from "../../hooks/useAuth";
import { AdminLoginView } from "./AdminLogin.view";

/**
 * Admin login step 1, the container. Auth, validation, toasts, the handoff to
 * the access code step, the redirects.
 *
 * Everything visual lives in `AdminLogin.view.jsx`, which is pure, so the state
 * explorer can mount the real screen without any of this running. See
 * `design-system/explorer/registry.js` for that convention.
 *
 * `AdminLogin.css` is gone. Nothing else imported it, and this page and
 * `AdminAccessCode.jsx` were its only two consumers.
 *
 * TWO DELIBERATE DIFFERENCES FROM THE PAGE THIS REPLACES
 * -----------------------------------------------------
 * 1. `loading` no longer blanks the whole page behind a centred spinner. It is
 *    the submit's own loading state now, which is what `AuthSubmit` exists for
 *    and what keeps the values the user typed on screen while the request runs.
 *
 * 2. A failed submission also sets an in-page banner, not only a toast. A toast
 *    is gone in four seconds and takes the reason with it, and on the one screen
 *    where the user has nothing else to read that is the wrong place for the
 *    only copy of the error. The toast still fires, unchanged.
 *
 * SECURITY, FLAGGED NOT CHANGED
 * -----------------------------
 * On success this writes `{ email, password }` to `sessionStorage` under
 * `adminLoginData`, so an administrator's plaintext password sits in web storage
 * for the length of the access code step. Step 2 only ever reads `.email` from
 * it, so the password does not need to be there at all. That is a security
 * question rather than a design one, so the behaviour is preserved exactly and
 * raised in the handoff instead of being quietly altered.
 */

const AdminLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { adminLoginStep1, loading, isAuthenticated } = useAuth();

  const [formData, setFormData] = useState({ email: "", password: "" });
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from?.pathname || "/marketplace";
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
    if (formError) setFormError("");
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }

    if (!formData.password.trim()) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      showToast.error("Please fix the errors below", "error");
      return;
    }

    try {
      const result = await adminLoginStep1(formData.email, formData.password);

      if (result.success) {
        // Credentials verified. Carry the email to the access code step.
        sessionStorage.setItem("adminLoginData", JSON.stringify(formData));
        setFormError("");
        showToast.success(
          result.message || "Credentials verified. Please enter access code.",
          "success"
        );
        navigate("/admin-login/access");
      } else {
        const reason = result.error || "Invalid email or password";
        setFormError(reason);
        showToast.error(reason, "error");
      }
    } catch (error) {
      const reason = "Login failed. Please try again.";
      setFormError(reason);
      showToast.error(reason, "error");
    }
  };

  return (
    <AdminLoginView
      values={formData}
      errors={errors}
      onChange={handleChange}
      onSubmit={handleSubmit}
      loading={loading}
      error={formError}
    />
  );
};

export default AdminLogin;
